import React, { useState, useCallback, useEffect } from "react";
import {
    Page,
    Layout,
    Card,
    Badge,
    BlockStack,
    InlineStack,
    Text,
    Icon,
    Banner,
    Toast as PolarisToast,
} from "@shopify/polaris";
import {
    ImageIcon,
    CodeIcon,
    CheckCircleIcon,
    DuplicateIcon,
} from "@shopify/polaris-icons";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, CartesianGrid, XAxis, YAxis, Bar } from "recharts";
import { DateRangePicker } from "../components/DateRangePicker";
import { config_variable } from "../helper/commonApi";
import { useDispatch, useSelector } from "react-redux";
import { getAllSetting, getAnalyticsAsync } from "../redux/slice/commonSlice";

const MainPage = () => {
    const dispatch = useDispatch();
    const { isEmbedEnabled, isAnalyticsLoading, analytics } = useSelector(getAllSetting);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const [date, setDate] = useState({
        start: today,
        end: tomorrow,
    });
    const [extensionEnabled, setExtensionEnabled] = useState(isEmbedEnabled || false);
    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const [protections, setProtections] = useState({
        contentCopy: true,
        devTool: true,
        rightClick: true,
    });

    useEffect(() => {
        if (date?.start && date?.end) {
            dispatch(getAnalyticsAsync({ start: date.start, end: date.end }));
        }
    }, [date, dispatch]);

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const handleEnableExtension = () => {
        window.open(
            `https://${config_variable.shop_name}/admin/themes/current/editor?context=apps&template=head&activateAppId=${config_variable?.config?.apiKey}/main`,
            "_blank"
        );
        setExtensionEnabled(true);
        showToast("LM - Store Protector extension is now active on your storefront.");
    };

    const chartData = analytics?.filterData || []

    const pieChartData = analytics?.totalData || [];

    const toastMarkup = toastActive ? (
        <PolarisToast content={toastMessage} onDismiss={() => setToastActive(false)} />
    ) : null;

    const getActionValue = (actionName) => {
        return analytics?.totalData?.find(i => i.name.includes(actionName))?.value || 0;
    };

    return (
        <Page title="LM - Store Protector" subtitle="Protect your store content">
            <BlockStack gap="400">
                {(!extensionEnabled && !isEmbedEnabled) ? (
                    <Banner
                        onDismiss={() => ""}
                        title="LM - Store Protector Extension Not Enabled"
                        tone="warning"
                        action={{
                            content: "Enable Extension",
                            onAction: handleEnableExtension,
                        }}
                    >
                        <p>
                            To activate protection, please enable the LM - Store Protector theme extension on your storefront theme.
                        </p>
                    </Banner>
                ) : (
                    <Banner title="LM - Protection Active" tone="success">
                        <p>Your store is protected with LM - Protection Active extension.</p>
                    </Banner>
                )}

                <Layout>
                    <Layout.Section variant="oneThird">
                        <Card>
                            <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="100" blockAlign="center">
                                        <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                            <Icon source={DuplicateIcon} tone="success" />
                                        </div>
                                        <Text variant="headingMd" as="h3" fontWeight="semibold">
                                            Content Copy
                                        </Text>
                                    </InlineStack>
                                    <Badge tone={protections.contentCopy ? "success" : "critical"}>
                                        {protections.contentCopy ? "Protected" : "Unprotected"}
                                    </Badge>
                                </InlineStack>

                                <Text as="p" tone="subdued" variant="bodySm">
                                    Prevents text content copying
                                </Text>

                                <InlineStack gap="200" blockAlign="center">
                                    <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                        <Icon source={CheckCircleIcon} tone="success" />
                                    </div>
                                    <Text as="span" variant="bodyLg" fontWeight="bold">
                                        {getActionValue("Text Copy")}
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        blocked
                                    </Text>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>

                    {/* Right Click Card */}
                    <Layout.Section variant="oneThird">
                        <Card>
                            <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="100" blockAlign="center">
                                        <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                            <Icon source={ImageIcon} tone="success" />
                                        </div>
                                        <Text variant="headingMd" as="h3" fontWeight="semibold">
                                            Right Click Protection
                                        </Text>
                                    </InlineStack>
                                    <Badge tone={protections.rightClick ? "success" : "critical"}>
                                        {protections.rightClick ? "Protected" : "Unprotected"}
                                    </Badge>
                                </InlineStack>

                                <Text as="p" tone="subdued" variant="bodySm">
                                    Prevents right click
                                </Text>

                                <InlineStack gap="200" blockAlign="center">
                                    <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                        <Icon source={CheckCircleIcon} tone="success" />
                                    </div>
                                    <Text as="span" variant="bodyLg" fontWeight="bold">
                                        {getActionValue("Right Click Trigger")}
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        blocked
                                    </Text>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>

                    {/* Dev Tool Card */}
                    <Layout.Section variant="oneThird">
                        <Card>
                            <BlockStack gap="400">
                                <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="100" blockAlign="center">
                                        <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                            <Icon source={CodeIcon} tone="success" />
                                        </div>
                                        <Text variant="headingMd" as="h3" fontWeight="semibold">
                                            DevTool
                                        </Text>
                                    </InlineStack>
                                    <Badge tone={protections.devTool ? "success" : "critical"}>
                                        {protections.devTool ? "Protected" : "Unprotected"}
                                    </Badge>
                                </InlineStack>

                                <Text as="p" tone="subdued" variant="bodySm">
                                    Blocks developer tools access
                                </Text>

                                <InlineStack gap="200" blockAlign="center">
                                    <div style={{ backgroundColor: "#EBF9EE", padding: "8px", borderRadius: "8px" }}>
                                        <Icon source={CheckCircleIcon} tone="success" />
                                    </div>
                                    <Text as="span" variant="bodyLg" fontWeight="bold">
                                        {getActionValue("Dev Tool Open")}
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        blocked
                                    </Text>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                </Layout>
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingMd" as="h2">
                                Protection Attempts Distribution
                            </Text>
                            <DateRangePicker
                                value={date}
                                onDateRangeSelect={({ start, end }) => setDate({ start, end })}
                            />
                        </InlineStack>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                            <div>
                                <Text variant="headingSm" as="h3" fontWeight="semibold">
                                    Protection Attempts
                                </Text>
                                <div style={{ width: "100%", height: "300px", marginTop: "16px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} barSize={60}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value">
                                                {chartData?.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div>
                                <Text variant="headingSm" as="h3" fontWeight="semibold">
                                    Distribution Overview
                                </Text>
                                <div style={{ width: "100%", height: "300px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, value }) => `${name}: ${value}`}
                                                outerRadius={80}
                                                dataKey="value"
                                            >
                                                {pieChartData?.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </BlockStack>
                </Card>
            </BlockStack>

            {toastMarkup}
            <br />
        </Page>
    );
};

export default MainPage;
